import { api } from "@/lib/api/client";
import type { EnrollmentStatus } from "@/lib/api/enrollments";
import { decodeCourseHomeProjectionV6, decodeLearningCourseContext, decodeTeachingCourseContext } from "./courseHomeV6Decoder";

export { decodeCourseHomeProjectionV6 } from "./courseHomeV6Decoder";

export type CourseHomePerspective = "teaching" | "learning";

export interface CourseHomeCourse {
  id: string;
  code: string;
  title: string;
  title_ar: string | null;
  crn: string | null;
  status: "draft" | "published";
  timezone: string;
  program: { id: string; name: string } | null;
  term: {
    id: string;
    name: string;
    status: "active" | "upcoming" | "closed";
    starts_on: string | null;
    ends_on: string | null;
  } | null;
}

export interface CourseHomeUnit {
  id: string;
  position: number;
  topic: string;
  /** The plan's session start, null when the plan is on no session yet. */
  starts_at: string | null;
}

export type CourseHomeSection<T> =
  | { state: "ready"; value: T }
  | { state: "empty" }
  | { state: "unavailable" };

export interface CourseHomeSession {
  id: string;
  label: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  institutional_status: "scheduled" | "held" | "cancelled";
  clock_phase: "upcoming" | "scheduled_now" | "status_pending";
  modality: "online" | "in_person" | "hybrid";
  readiness: "ready" | "missing" | "invalid" | "in_person" | "cancelled" | "held";
  action: { id: "open_session"; href: string };
}

export interface CourseHomeSequence {
  phase: "before_course" | "in_progress" | "after_course";
  current: CourseHomeUnit | null;
  next: CourseHomeUnit | null;
}

/**
 * Issue #441. The server's one decision about what this person should do next.
 *
 * `label` and `href` are rendered VERBATIM. The browser does not rank, relabel,
 * or reprioritize: the ranker is the server's, and a second ordering here would
 * be a second product rule that drifts from the projection it is drawn from.
 */
export interface TeachingPrimaryAction {
  id: "open_class" | "review_attempt" | "open_assignment";
  reason: "class_in_progress" | "oldest_review" | "next_class" | "next_deadline";
  label: string;
  href: string;
}

export interface LearningPrimaryAction {
  id: "open_class" | "open_assignment";
  reason: "class_in_progress" | "returned_revision" | "due_unattempted" | "next_class" | "evidence";
  label: string;
  href: string;
}

export type CourseHomeLearnerState =
  | "unattempted"
  | "submitted"
  | "resubmitted"
  | "returned"
  | "graded";

export interface CourseHomeActionV6<Id extends string, Label extends string> {
  id: Id;
  href: string;
  label: Label;
}

export type TeachingCourseworkDestinationV6 =
  | CourseHomeActionV6<"open_assignment", "Open assignment">
  | CourseHomeActionV6<"review_attempt", "Review attempt">;

export type LearningCourseworkDestinationV6 =
  | CourseHomeActionV6<"open_assignment", "Open assignment">
  | CourseHomeActionV6<"view_receipt", "View receipt">
  | CourseHomeActionV6<"view_feedback", "View feedback">;

/** The session the course page opens: the same row on either seat (#794). */
export interface CourseHomeOccurrenceV6 extends Omit<CourseHomeSession, "action"> {
  row_id: string;
  location: string | null;
  destination: CourseHomeActionV6<"open_session", "Open session">;
}

/** Recorded session-plan placement; null is the whole course. The topic is the teaching team's only. */
export interface CourseworkPlaceV6 {
  unit_id: string;
  unit_title?: string | null;
  session_id?: string | null;
  session_no?: number | null;
}

interface CourseworkRowV6Base {
  place?: CourseworkPlaceV6 | null;
  quiz_mode?: "graded" | "practice" | null;
  quiz_result_mode?: "graded" | "practice" | null;
  work_required?: boolean;
  /** How a recipient's attempt arrives; null when the teacher records it in class. */
  learner_act?: "hand_in" | "mark_done" | "mark_taken" | null;
  row_id: string;
  assignment_id: string;
  title: string;
  due_at: string | null;
  points: string | null;
}

export interface TeachingCourseworkRowV6 extends CourseworkRowV6Base {
  archived_at?: string | null;
  status: "draft" | "published" | "closed";
  coverage: {
    enrolled_count: number;
    submitted_count: number;
    awaiting_review_count: number;
  awaiting_release_count?: number;
  };
  destination: TeachingCourseworkDestinationV6;
}

export interface LearningCourseworkRowV6 extends CourseworkRowV6Base {
  status: "published" | "closed";
  learner_state: CourseHomeLearnerState;
  receipt: { attempt_number: number; submitted_at: string } | null;
  feedback_released_at: string | null;
  resubmission_open: boolean;
  destination: LearningCourseworkDestinationV6;
}

/** The retired loop's stored history: read, nothing stored, or unreadable (ADR 0026 as amended). */
export type ClassroomObservationState = "ready" | "empty" | "unavailable";

export type ClassroomPermittedAction =
  | "open" | "submission_turn_in" | "submission_reclaim" | "grade_patch"
  | "submission_return" | "result_proposal";

export interface ClassroomSubmissionV6 {
  institutional_id: string;
  provider_submission_id: string;
  enrollment_id: string;
  learner_display_name: string | null;
  state: "NEW" | "CREATED" | "TURNED_IN" | "RETURNED" | "RECLAIMED_BY_STUDENT";
  copy_state: "ready" | "delayed_copy" | "not_observed" | "unavailable";
  provider_url: string | null;
  copy_url: string | null;
  copy_title: string | null;
  history?: Array<{ state: "TURNED_IN" | "RETURNED" | "RECLAIMED_BY_STUDENT"; at: string }>;
  draft_grade: number | null;
  assigned_grade: number | null;
  last_successful_at: string | null;
  unavailable_at: string | null;
  submitted_at: string | null;
  /** The learner's enrollment now: history keeps a withdrawn learner's record. */
  enrollment_status: EnrollmentStatus;
  destination: CourseHomeActionV6<
    "open_classroom_submission",
    "Open record" | "Open your record"
  >;
  permitted_actions: ClassroomPermittedAction[];
}

interface ClassroomCourseworkV6Base {
  materials?: Array<{ title: string; url: string }>;
  owner: "classroom";
  row_id: string;
  coursework_id: string;
  provider_coursework_id: string;
  provenance: "app_created" | "provider_discovered";
  provider_write_eligible: boolean;
  title: string;
  instructions: string | null;
  status: "DRAFT" | "PUBLISHED";
  due_at: string | null;
  points: number | null;
  provider_url: string | null;
  last_successful_at: string | null;
  unavailable_at: string | null;
  /** A later references read that found the item posted while the record's own read held it unposted. */
  posted_read_at?: string | null;
  /** The records the loop stored, never the course's roster. */
  coverage: { recorded_count: number; submitted_count: number };
  destination:
    | CourseHomeActionV6<"open_classroom_coursework", "Open record">
    | CourseHomeActionV6<
        "open_classroom_submission",
        "Open record" | "Open your record"
      >;
  permitted_actions: Array<"open">;
}

export interface TeachingClassroomCourseworkV6 extends ClassroomCourseworkV6Base {
  /** Whom Classroom addressed: the course, or a counted set of learners. */
  audience: { mode: "course_wide" | "individual_students"; learner_count: number | null };
  submissions: ClassroomSubmissionV6[];
}

export interface LearningClassroomCourseworkV6 extends ClassroomCourseworkV6Base {
  /** How the work reaches this learner: course-wide, addressed to them, or only through their own stored record. */
  audience: { mode: "course_wide" | "addressed_to_you" | "own_record" };
  submission: ClassroomSubmissionV6 | null;
}

export interface ClassroomRecoveryV6 {
  actor: "instructor" | "learner" | "administrator";
  reason: string;
  destination:
    | CourseHomeActionV6<"open_course_connections", "Open course connections">
    | CourseHomeActionV6<"open_classroom_recovery", "Open Classroom recovery">
    | CourseHomeActionV6<"open_classroom_submission", "Open your record">
    | null;
}

interface ClassroomV6Base {
  enabled: true;
  owner: "classroom";
  account_label: string | null;
  course_name: string | null;
  observation: {
    state: ClassroomObservationState;
    last_successful_at: string | null;
    unavailable_at: string | null;
  };
  recovery: ClassroomRecoveryV6 | null;
}

export type TeachingClassroomV6 = { enabled: false } | ClassroomV6Base & {
  create_destination: null;
  items: TeachingClassroomCourseworkV6[];
};

export type LearningClassroomV6 = { enabled: false } | ClassroomV6Base & {
  create_destination: null;
  items: LearningClassroomCourseworkV6[];
};

/** The course page reads the course, the session it opens, the plan sequence,
 * and the course's work; v6 carries nothing else and is the only projection (#794). */
interface CourseHomeProjectionV6Base {
  projection_version: "course-home-v6";
  recording_policy?: import("./tenant").RecordingPolicy | null;
  perspective: CourseHomePerspective;
  generated_at: string;
  course: CourseHomeCourse;
  session: CourseHomeSection<CourseHomeOccurrenceV6>;
  sequence: CourseHomeSection<CourseHomeSequence>;
}

export interface TeachingCourseHomeProjectionV6 extends CourseHomeProjectionV6Base {
  perspective: "teaching";
  relationship: {
    kind: "primary" | "co_instructor";
    teaching_assignment_id: string;
  };
  coursework: CourseHomeSection<{ items: TeachingCourseworkRowV6[] }>;
  classroom: TeachingClassroomV6;
}

export interface LearningCourseHomeProjectionV6 extends CourseHomeProjectionV6Base {
  perspective: "learning";
  relationship: {
    kind: "enrollment";
    enrollment_id: string;
    participation_kind: "credit" | "audit";
  };
  coursework: CourseHomeSection<{ items: LearningCourseworkRowV6[] }>;
  classroom: LearningClassroomV6;
}

export type CourseHomeProjectionV6 = TeachingCourseHomeProjectionV6 | LearningCourseHomeProjectionV6;

export type TeachingCourseHomeProjection = TeachingCourseHomeProjectionV6;
export type LearningCourseHomeProjection = LearningCourseHomeProjectionV6;
export type CourseHomeProjection = CourseHomeProjectionV6;

type RecordCourse = Omit<CourseHomeCourse, "status"> & { status: "draft" | "published" | "archived" };
export type LearningCourseContext = { course: RecordCourse; relationship: LearningCourseHomeProjectionV6["relationship"] & { enrollment_status?: "enrolled" | "incomplete" | "complete" } };
export type TeachingCourseContext = { course: RecordCourse; relationship: TeachingCourseHomeProjectionV6["relationship"] };

export const courseHomeApi = {
  getTeachingContext: async (courseId: string): Promise<TeachingCourseContext> =>
    decodeTeachingCourseContext(await api.get<unknown>(`/courses/${courseId}/teaching-context`), courseId),
  getLearningContext: async (courseId: string): Promise<LearningCourseContext> =>
    decodeLearningCourseContext(await api.get<unknown>(`/courses/${courseId}/learning-context`), courseId),
  getTeaching: async (courseId: string): Promise<TeachingCourseHomeProjection> =>
    decodeCourseHomeProjectionV6(await api.get<unknown>(`/courses/${courseId}/teaching-workspace`), "teaching"),
  getLearning: async (courseId: string): Promise<LearningCourseHomeProjection> =>
    decodeCourseHomeProjectionV6(await api.get<unknown>(`/courses/${courseId}/learning-workspace`), "learning"),
};
